import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { POLL_CURATION_MS } from "@/lib/realtime";
import { useAuthStore } from "@/stores/authStore";
import {
  useCreateMemory,
  useDeleteMemory,
  usePreviewMemoryImport,
  useSaveMemoryImport,
  useUpdateMemory,
  type MemoryList,
} from "./memoryMutations";

export type UserMemorySource = "manual" | "chat" | "import";

/** Personal memory: notes about the signed-in user, private to them, global to every brand. */
export type UserMemory = {
  id: string;
  body: string;
  source: UserMemorySource;
  createdAt: string;
  updatedAt: string;
};

/** Invalidate this prefix to refresh whoever is signed in. */
export const USER_MEMORIES_ROOT = ["user-memories"] as const;

// Keyed on the user: the query cache outlives a logout, and one person's
// memories must never render for the next.
export const userMemoriesKey = (userId: string | null) =>
  [...USER_MEMORIES_ROOT, userId] as const;

const BASE_PATH = "/user-memories";

function useTarget() {
  const userId = useAuthStore((state) => state.user?.id ?? null);
  return { key: userMemoriesKey(userId), basePath: userId ? BASE_PATH : null, userId };
}

export function useUserMemories() {
  const { key, userId } = useTarget();
  return useQuery({
    queryKey: key,
    enabled: Boolean(userId),
    queryFn: async () => {
      const res = await axiosInstance.get<MemoryList<UserMemory>>(BASE_PATH);
      return res.data;
    },
    // SavePersonalMemory writes from any chat, in any tab.
    refetchInterval: POLL_CURATION_MS,
  });
}

export function useCreateUserMemory() {
  return useCreateMemory<UserMemory>(useTarget(), { source: "manual" });
}

export function useUpdateUserMemory() {
  return useUpdateMemory<UserMemory>(useTarget());
}

export function useDeleteUserMemory() {
  return useDeleteMemory<UserMemory>(useTarget());
}

export function usePreviewUserMemoryImport() {
  return usePreviewMemoryImport(useTarget());
}

export function useSaveUserMemoryImport() {
  return useSaveMemoryImport<UserMemory>(useTarget(), { source: "import" });
}
